/**
 * SM-14 "Play together" — the in-chat mini-game layer (V10 web client,
 * v5 "PIXEL SWEETHEART": Ticker HOSTS every board).
 *
 * Presentation + wiring only — the server is the referee (SM §0): every tap
 * is POST /games/{id}/act|respond, every render is a viewer-safe GameView
 * from REST or the game.* WS pushes. Nothing here computes an outcome; on a
 * 409 the stale card re-syncs itself with GET /games/{id}.
 *
 * Pieces:
 *  - useMatchGames(matchId)   REST recovery plane + live game.* subscription
 *  - <PlayTool/>              the pixel-gamepad composer button
 *  - <GamePickerSheet/>       kind picker; two_truths collects the 3
 *                             statements + the lie BEFORE the invite POSTs
 *                             (#66b send-anyway interstitial mirrors chat)
 *  - <GameThreadCard/>        the in-thread card: invite → board → result,
 *                             plus the tasteful closed states and the report
 *                             affordance (safety never jokes)
 *
 * A11y: boards are plain buttons (keyboard playable), reveal beats carry
 * text markers (never color alone), bursts honor prefers-reduced-motion.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { copy, formatCopy, resolveCopy } from "@humble/shared";
import {
  Button,
  Mascot,
  PixelCross,
  PixelGlyph,
  PixelHeart,
  PixelHeartCracked,
  PixelSparkle,
  Sheet,
  bounceIn,
  springs,
  toast,
  usePrefersReducedMotion,
  type MascotMood,
  type PixelGlyphProps,
} from "../../design/index.js";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import "./games.css";

// ---------------------------------------------------------------------------
// wire shapes (SM-14 GameView — REST bodies AND the game.* WS payloads)
// ---------------------------------------------------------------------------

export type GameKindWire = "this_or_that" | "two_truths" | "tictactoe";
export type GameStatusWire = "invited" | "active" | "completed" | "declined" | "expired" | "abandoned";

interface ThisOrThatPromptWire {
  id: number;
  option_a: string;
  option_b: string;
}

interface RevealedRoundWire {
  round: number;
  prompt: ThisOrThatPromptWire;
  /** userId → pick; both present (rounds reveal only when both submitted). */
  picks: Record<string, "a" | "b">;
  agreed: boolean;
}

export interface GameWire {
  game_id: string;
  match_id: string;
  kind: GameKindWire;
  status: GameStatusWire;
  created_by: string;
  turn_user: string | null;
  you: { is_creator: boolean; your_turn: boolean };
  this_or_that?: {
    total_rounds: number;
    round: number;
    current: ThisOrThatPromptWire | null;
    your_pick: "a" | "b" | null;
    partner_picked: boolean;
    revealed: RevealedRoundWire[];
  };
  two_truths?: { statements: string[]; folded: boolean[] };
  tictactoe?: { board: ("x" | "o" | null)[]; x_user: string; o_user: string };
  result: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

const KIND_LABELS: Record<GameKindWire, string> = {
  this_or_that: copy.games.this_or_that_label,
  two_truths: copy.games.two_truths_label,
  tictactoe: copy.games.tictactoe_label,
};

// ---------------------------------------------------------------------------
// pixel gamepad — the Play glyph (local to the game layer; v5 §1 seasoning)
// ---------------------------------------------------------------------------

const GAMEPAD: readonly string[] = [
  "..XXXXXXXXXX..",
  ".XXXXXXXXXXXX.",
  "XXXXXXXXXXXXXX",
  "XXX.XXXXXX.XXX",
  "XX...XXXX.X.XX",
  "XXX.XXXXXX.XXX",
  "XXXXXXXXXXXXXX",
  ".XXXX....XXXX.",
  "..XXX....XXX..",
];

export function PixelGamepad(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={GAMEPAD} {...props} />;
}

// ---------------------------------------------------------------------------
// transport helpers — REST twins of the game.* pushes (server = referee)
// ---------------------------------------------------------------------------

/** Toast the friendly line for a game-plane API error; returns the code. */
function toastGameError(err: unknown): string {
  if (!isApiError(err)) {
    toast(err instanceof Error ? err.message : "Something broke.", "error");
    return "INTERNAL";
  }
  const KEYS: Record<string, { key: string; kind: "info" | "error" }> = {
    NOT_YOUR_TURN: { key: "games.not_your_turn", kind: "info" },
    ALREADY_PICKED: { key: "games.already_picked", kind: "info" },
    GAME_OVER: { key: "games.over", kind: "info" },
    NOT_INVITED: { key: "games.over", kind: "info" },
    NOT_YOUR_INVITE: { key: "games.not_your_invite", kind: "info" },
    GAME_ALREADY_LIVE: { key: "games.already_live", kind: "info" },
    RATE_LIMITED: { key: "games.rate_limited", kind: "info" },
    ILLEGAL_MOVE: { key: "games.illegal_move", kind: "info" },
    OPENER_RIGHTS: { key: "games.opener_gate", kind: "info" },
    BERSERK_OVER: { key: "games.opener_gate", kind: "info" },
    PAIR_CLOSED: { key: "chat.closed", kind: "error" },
    MATCH_EXPIRED: { key: "chat.match_expired", kind: "error" },
  };
  const hit = KEYS[err.code];
  toast(hit ? (resolveCopy(hit.key) ?? err.message) : err.message, hit?.kind ?? "error");
  return err.code;
}

/** Re-sync one card from the REST recovery plane (after a stale 409). */
async function refreshGame(gameId: string, upsert: (g: GameWire) => void): Promise<void> {
  try {
    const view = (await api.get("/games/{gameId}", { params: { gameId } })) as unknown as GameWire;
    if (view?.game_id) upsert(view);
  } catch {
    /* the WS plane will catch the card up */
  }
}

const STALE_CODES = new Set(["NOT_YOUR_TURN", "ALREADY_PICKED", "GAME_OVER", "NOT_INVITED", "ILLEGAL_MOVE"]);

async function postRespond(
  gameId: string,
  action: "accept" | "decline",
  upsert: (g: GameWire) => void,
): Promise<void> {
  try {
    const view = (await api.post("/games/{gameId}/respond", {
      params: { gameId },
      body: withEventId<{ client_event_id: string; action: "accept" | "decline" }>({ action }) as never,
    })) as unknown as GameWire;
    if (view?.game_id) upsert(view);
  } catch (err) {
    const code = toastGameError(err);
    if (STALE_CODES.has(code)) void refreshGame(gameId, upsert);
  }
}

async function postAct(
  gameId: string,
  move: { pick?: "a" | "b"; guess_index?: number; cell?: number },
  upsert: (g: GameWire) => void,
): Promise<void> {
  try {
    const view = (await api.post("/games/{gameId}/act", {
      params: { gameId },
      body: withEventId<{
        client_event_id: string;
        move: { pick?: "a" | "b"; guess_index?: number; cell?: number };
      }>({ move }) as never,
    })) as unknown as GameWire;
    if (view?.game_id) upsert(view);
  } catch (err) {
    const code = toastGameError(err);
    if (STALE_CODES.has(code)) void refreshGame(gameId, upsert);
  }
}

// ---------------------------------------------------------------------------
// useMatchGames — REST recovery plane on mount + live game.* subscription
// ---------------------------------------------------------------------------

export function useMatchGames(matchId: string | undefined): {
  games: GameWire[];
  upsertGame: (g: GameWire) => void;
} {
  const [games, setGames] = useState<GameWire[]>([]);

  const upsertGame = useCallback((g: GameWire) => {
    setGames((prev) => {
      const i = prev.findIndex((x) => x.game_id === g.game_id);
      if (i === -1) return [...prev, g];
      // WS pushes race the REST echo — keep whichever view is newer.
      if (prev[i]!.updated_at > g.updated_at) return prev;
      const next = prev.slice();
      next[i] = g;
      return next;
    });
  }, []);

  useEffect(() => {
    setGames([]);
    if (!matchId) return;
    let alive = true;
    void api
      .get("/matches/{matchId}/games", { params: { matchId } })
      .then((res) => {
        const rows = (res as unknown as { games?: GameWire[] })?.games;
        if (alive && Array.isArray(rows)) setGames(rows);
      })
      .catch(() => {
        /* the thread renders without history; WS keeps the live game honest */
      });
    // Push handlers MUST re-render server state, never compute an outcome
    // (lib/ws.ts contract) — each frame carries the full viewer-safe view.
    const onGame = (p: unknown) => {
      const { match_id, game } = (p ?? {}) as { match_id?: string; game?: GameWire };
      if (match_id !== matchId || !game?.game_id) return;
      upsertGame(game);
    };
    const offs = [
      ws.on("game.invited", onGame),
      ws.on("game.updated", onGame),
      ws.on("game.completed", onGame),
    ];
    return () => {
      alive = false;
      offs.forEach((off) => off());
    };
  }, [matchId, upsertGame]);

  return { games, upsertGame };
}

// ---------------------------------------------------------------------------
// <GamePickerSheet/> — the Play entry: pick a game, Ticker sells it.
// two_truths collects statements + the lie BEFORE the invite POSTs.
// ---------------------------------------------------------------------------

export interface GamePickerSheetProps {
  open: boolean;
  onClose: () => void;
  matchId: string;
  /** One live game per pair — the picker says so instead of a 409 surprise. */
  hasLiveGame: boolean;
  onCreated: (game: GameWire) => void;
}

const PICKER_KINDS: ReadonlyArray<{ kind: GameKindWire; descKey: string }> = [
  { kind: "this_or_that", descKey: "games.this_or_that_desc" },
  { kind: "two_truths", descKey: "games.two_truths_desc" },
  { kind: "tictactoe", descKey: "games.tictactoe_desc" },
];

function KindIcon({ kind, size = 22 }: { kind: GameKindWire; size?: number }) {
  if (kind === "this_or_that") {
    return (
      <span className="gp__icon-pair" aria-hidden="true">
        <PixelHeart size={size * 0.72} />
        <PixelHeartCracked size={size * 0.72} />
      </span>
    );
  }
  if (kind === "two_truths") return <PixelSparkle size={size} />;
  return <PixelCross size={size} />;
}

export function GamePickerSheet({ open, onClose, matchId, hasLiveGame, onCreated }: GamePickerSheetProps) {
  const [step, setStep] = useState<"pick" | "two_truths">("pick");
  const [statements, setStatements] = useState<string[]>(["", "", ""]);
  const [lieIndex, setLieIndex] = useState<number | null>(null);
  const [needsAnyway, setNeedsAnyway] = useState(false);
  const [busyKind, setBusyKind] = useState<GameKindWire | null>(null);

  // Reset the flow whenever the sheet reopens.
  useEffect(() => {
    if (open) {
      setStep("pick");
      setStatements(["", "", ""]);
      setLieIndex(null);
      setNeedsAnyway(false);
      setBusyKind(null);
    }
  }, [open]);

  const create = useCallback(
    async (kind: GameKindWire, extra?: { statements: string[]; lie_index: number; send_anyway?: boolean }) => {
      setBusyKind(kind);
      try {
        const body = withEventId<{
          client_event_id: string;
          kind: GameKindWire;
          statements?: string[];
          lie_index?: number;
          send_anyway?: boolean;
        }>({ kind, ...(extra ?? {}) });
        const view = (await api.post("/matches/{matchId}/games", {
          params: { matchId },
          body: body as never,
        })) as unknown as GameWire;
        if (view?.game_id) onCreated(view);
      } catch (err) {
        if (isApiError(err, "SEND_ANYWAY_REQUIRED")) {
          setNeedsAnyway(true); // #66b — dead serious, mirrors the chat interstitial
        } else {
          toastGameError(err);
        }
      } finally {
        setBusyKind(null);
      }
    },
    [matchId, onCreated],
  );

  const ttReady = statements.every((s) => s.trim().length > 0) && lieIndex !== null;

  return (
    <Sheet open={open} onClose={onClose} title={resolveCopy("games.picker_title") ?? "Play together"}>
      <div className="gp" data-testid="game-picker">
        {step === "pick" && (
          <>
            <div className="gp__host">
              <Mascot mood="happy" size={34} />
              <p className="gp__lede type-editorial text-dim">{resolveCopy("games.picker_lede")}</p>
            </div>
            {hasLiveGame && <p className="gp__note text-dim">{resolveCopy("games.already_live")}</p>}
            <div className="gp__options">
              {PICKER_KINDS.map(({ kind, descKey }) => (
                <button
                  key={kind}
                  className="gp__option"
                  data-testid={`game-pick-${kind}`}
                  disabled={hasLiveGame || busyKind !== null}
                  onClick={() => {
                    if (kind === "two_truths") setStep("two_truths");
                    else void create(kind);
                  }}
                >
                  <span className="gp__option-icon" aria-hidden="true">
                    <KindIcon kind={kind} />
                  </span>
                  <span className="gp__option-main">
                    <span className="gp__option-label type-pixel">{KIND_LABELS[kind]}</span>
                    <span className="gp__option-desc text-dim">{resolveCopy(descKey)}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "two_truths" && !needsAnyway && (
          <div className="gp__tt">
            <div className="gp__host">
              <Mascot mood="wink" size={30} />
              <p className="gp__lede type-editorial text-dim">{resolveCopy("games.tt_compose_lede")}</p>
            </div>
            <fieldset className="gp__tt-fields">
              <legend className="gp__tt-legend text-faint">{resolveCopy("games.tt_mark_lie_label")}</legend>
              {statements.map((value, i) => (
                <div className="gp__tt-row" key={i}>
                  <textarea
                    className="gp__tt-input"
                    data-testid={`tt-input-${i}`}
                    rows={2}
                    maxLength={280}
                    placeholder={formatCopy(resolveCopy("games.tt_statement_placeholder") ?? "Statement {n}", {
                      n: i + 1,
                    })}
                    value={value}
                    onChange={(e) =>
                      setStatements((prev) => prev.map((s, j) => (j === i ? e.target.value : s)))
                    }
                  />
                  <label className={`gp__tt-lie ${lieIndex === i ? "gp__tt-lie--on" : ""}`}>
                    <input
                      type="radio"
                      name="tt-lie"
                      data-testid={`tt-lie-${i}`}
                      checked={lieIndex === i}
                      onChange={() => setLieIndex(i)}
                    />
                    <span className="type-pixel">{resolveCopy("games.tt_lie_marker")}</span>
                  </label>
                </div>
              ))}
            </fieldset>
            <div className="gp__row">
              <Button variant="ghost" size="sm" onClick={() => setStep("pick")}>
                {resolveCopy("games.tt_back_label") ?? "Back"}
              </Button>
              <Button
                variant="primary"
                size="sm"
                data-testid="tt-send"
                loading={busyKind !== null}
                disabled={!ttReady}
                onClick={() => {
                  if (lieIndex === null) return;
                  void create("two_truths", {
                    statements: statements.map((s) => s.trim()),
                    lie_index: lieIndex,
                  });
                }}
              >
                {resolveCopy("games.tt_send_label")}
              </Button>
            </div>
          </div>
        )}

        {/* #66b send-anyway interstitial — dead serious, no humor (MISSION §2) */}
        {step === "two_truths" && needsAnyway && (
          <div className="gp__anyway">
            <p>{resolveCopy("chat.send_anyway")}</p>
            <div className="gp__row">
              <Button variant="ghost" size="sm" onClick={() => setNeedsAnyway(false)}>
                Edit message
              </Button>
              <Button
                variant="danger"
                size="sm"
                loading={busyKind !== null}
                onClick={() => {
                  if (lieIndex === null) return;
                  void create("two_truths", {
                    statements: statements.map((s) => s.trim()),
                    lie_index: lieIndex,
                    send_anyway: true,
                  }).then(() => setNeedsAnyway(false));
                }}
              >
                Send anyway
              </Button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// celebration — a contained pixel-heart burst (reduced-motion → nothing)
// ---------------------------------------------------------------------------

const BURST_COLORS = ["var(--accent)", "var(--gold)", "var(--like)", "var(--sr)", "var(--reject)"];

function ResultBurst() {
  const reduced = usePrefersReducedMotion();
  const pieces = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        left: 8 + Math.random() * 84,
        delay: Math.random() * 0.35,
        rise: 46 + Math.random() * 54,
        drift: (Math.random() - 0.5) * 44,
        rotate: (Math.random() - 0.5) * 300,
        color: BURST_COLORS[i % BURST_COLORS.length]!,
        heart: i % 4 === 0,
        scale: 0.6 + Math.random() * 0.8,
      })),
    [],
  );
  if (reduced) return null;
  return (
    <div className="gamecard__burst" aria-hidden="true">
      {pieces.map((p, i) => (
        <motion.span
          key={i}
          className={`gamecard__burst-bit${p.heart ? " gamecard__burst-bit--heart" : ""}`}
          style={
            p.heart
              ? { left: `${p.left}%`, color: p.color, scale: p.scale }
              : { left: `${p.left}%`, background: p.color, scale: p.scale }
          }
          initial={{ y: 0, opacity: 0, rotate: 0 }}
          animate={{ y: [0, -p.rise, -p.rise + 18], x: p.drift, opacity: [0, 1, 0], rotate: p.rotate }}
          transition={{ duration: 1.15, delay: p.delay, ease: "easeOut" }}
        >
          {p.heart && <PixelHeart size={10} />}
        </motion.span>
      ))}
    </div>
  );
}

/** Celebrate only completions that just happened — never on history reload. */
function justHappened(game: GameWire): boolean {
  return Date.now() - new Date(game.updated_at).getTime() < 15_000;
}

// ---------------------------------------------------------------------------
// boards
// ---------------------------------------------------------------------------

interface BoardProps {
  game: GameWire;
  myId: string;
  partnerName: string;
  busy: boolean;
  act: (move: { pick?: "a" | "b"; guess_index?: number; cell?: number }) => void;
}

/** Progress hearts over the 5 rounds — filled (agreed), cracked (differed),
 *  pulsing outline (current), faint (upcoming). Text lives in aria-label. */
function TotProgress({ game }: { game: GameWire }) {
  const tot = game.this_or_that!;
  const done = game.status === "completed";
  return (
    <div
      className="tot__progress"
      data-testid="tot-progress"
      role="img"
      aria-label={formatCopy(resolveCopy("games.round_counter") ?? "Round {n} of {total}", {
        n: Math.min(tot.round + 1, tot.total_rounds),
        total: tot.total_rounds,
      })}
    >
      {Array.from({ length: tot.total_rounds }, (_, i) => {
        const r = tot.revealed.find((x) => x.round === i);
        if (r) {
          return r.agreed ? (
            <span key={i} className="tot__pip tot__pip--agreed" aria-hidden="true">
              <PixelHeart size={13} />
            </span>
          ) : (
            <span key={i} className="tot__pip tot__pip--differed" aria-hidden="true">
              <PixelHeartCracked size={13} />
            </span>
          );
        }
        const current = !done && i === tot.round;
        return (
          <span key={i} className={`tot__pip ${current ? "tot__pip--current" : "tot__pip--todo"}`} aria-hidden="true">
            <PixelHeart size={13} />
          </span>
        );
      })}
    </div>
  );
}

function TotRevealRow({ r, myId, isLatest }: { r: RevealedRoundWire; myId: string; isLatest: boolean }) {
  const youMark = resolveCopy("games.you_marker") ?? "you";
  const themMark = resolveCopy("games.them_marker") ?? "them";
  const mine = r.picks[myId];
  const chip = (side: "a" | "b", label: string) => {
    const tags: string[] = [];
    if (mine === side) tags.push(youMark);
    if (Object.entries(r.picks).some(([uid, p]) => uid !== myId && p === side)) tags.push(themMark);
    return (
      <span className={`tot__chip ${tags.length > 0 ? "tot__chip--picked" : ""}`}>
        {label}
        {tags.map((t) => (
          <em key={t} className="tot__tag type-pixel">
            {t}
          </em>
        ))}
      </span>
    );
  };
  const row = (
    <div className={`tot__reveal ${r.agreed ? "tot__reveal--agreed" : ""}`}>
      <span className="tot__reveal-icon" aria-hidden="true">
        {r.agreed ? <PixelHeart size={12} /> : <PixelHeartCracked size={12} />}
      </span>
      {chip("a", r.prompt.option_a)}
      {chip("b", r.prompt.option_b)}
      {isLatest && (
        <span className="tot__beat text-faint">
          {resolveCopy(r.agreed ? "games.reveal_agree" : "games.reveal_differ")}
        </span>
      )}
    </div>
  );
  return isLatest ? (
    <motion.div variants={bounceIn} initial="initial" animate="enter">
      {row}
    </motion.div>
  ) : (
    row
  );
}

function ThisOrThatBoard({ game, myId, busy, act }: BoardProps) {
  const tot = game.this_or_that!;
  const picked = tot.your_pick !== null;
  const statusLine = picked
    ? resolveCopy("games.waiting_partner")
    : tot.partner_picked
      ? resolveCopy("games.partner_locked")
      : resolveCopy("games.your_move");
  return (
    <div className="tot">
      <TotProgress game={game} />
      {tot.current && (
        <>
          <div className="tot__options" role="group" aria-label={KIND_LABELS.this_or_that}>
            <button
              className={`tot__option ${tot.your_pick === "a" ? "tot__option--picked" : ""}`}
              data-testid="tot-option-a"
              disabled={picked || busy}
              onClick={() => act({ pick: "a" })}
            >
              {tot.current.option_a}
            </button>
            <span className="tot__vs type-pixel" aria-hidden="true">
              or
            </span>
            <button
              className={`tot__option ${tot.your_pick === "b" ? "tot__option--picked" : ""}`}
              data-testid="tot-option-b"
              disabled={picked || busy}
              onClick={() => act({ pick: "b" })}
            >
              {tot.current.option_b}
            </button>
          </div>
          <p className="gamecard__status text-dim">{statusLine}</p>
        </>
      )}
      {tot.revealed.length > 0 && (
        <div className="tot__history">
          {tot.revealed.map((r, i) => (
            <TotRevealRow key={r.round} r={r} myId={myId} isLatest={i === tot.revealed.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function TwoTruthsBoard({ game, busy, act }: BoardProps) {
  const tt = game.two_truths!;
  const guesser = !game.you.is_creator;
  const [unfolded, setUnfolded] = useState<Set<number>>(() => new Set());
  const [selected, setSelected] = useState<number | null>(null);

  if (!guesser) {
    return (
      <div className="tt">
        <ol className="tt__list">
          {tt.statements.map((s, i) => (
            <li key={i} className="tt__statement tt__statement--readonly">
              {s}
            </li>
          ))}
        </ol>
        <p className="gamecard__status text-dim">{resolveCopy("games.tt_creator_waiting")}</p>
      </div>
    );
  }
  return (
    <div className="tt">
      <p className="gamecard__prompt type-editorial">{resolveCopy("games.two_truths_prompt")}</p>
      <div className="tt__list" role="group" aria-label={KIND_LABELS.two_truths}>
        {tt.statements.map((s, i) => {
          const folded = Boolean(tt.folded[i]) && !unfolded.has(i);
          if (folded) {
            // #66a Hidden Words — same fold treatment as chat bubbles; the
            // first tap only reveals, it never guesses.
            return (
              <button
                key={i}
                className="tt__statement tt__statement--folded"
                data-testid={`tt-statement-${i}`}
                onClick={() => setUnfolded((prev) => new Set(prev).add(i))}
              >
                {resolveCopy("chat.hidden_by_filters")}{" "}
                <span className="text-faint">{resolveCopy("chat.tap_to_reveal") ?? "tap to reveal"}</span>
              </button>
            );
          }
          return (
            <button
              key={i}
              className={`tt__statement ${selected === i ? "tt__statement--selected" : ""}`}
              data-testid={`tt-statement-${i}`}
              aria-pressed={selected === i}
              disabled={busy}
              onClick={() => setSelected(i)}
            >
              {s}
            </button>
          );
        })}
      </div>
      <Button
        variant="primary"
        size="sm"
        data-testid="tt-guess-confirm"
        disabled={selected === null}
        loading={busy}
        onClick={() => {
          if (selected !== null) act({ guess_index: selected });
        }}
      >
        {resolveCopy("games.tt_guess_confirm")}
      </Button>
    </div>
  );
}

function TicTacToeBoard({ game, myId, busy, act }: BoardProps) {
  const ttt = game.tictactoe!;
  const active = game.status === "active";
  const myMark = ttt.x_user === myId ? "X" : "O";
  const winLine = (game.result?.line as number[] | null | undefined) ?? null;
  return (
    <div className="ttt">
      <p className="ttt__legend">
        <span className="type-pixel">
          {formatCopy(resolveCopy("games.ttt_you_are") ?? "You are {mark}.", { mark: myMark })}
        </span>
        {active && (
          <span className="text-dim">
            {" "}
            {resolveCopy(game.you.your_turn ? "games.your_move" : "games.their_move")}
          </span>
        )}
      </p>
      <div className="ttt__grid" role="group" aria-label={KIND_LABELS.tictactoe}>
        {ttt.board.map((cell, i) => (
          <button
            key={i}
            className={[
              "ttt__cell",
              cell ? `ttt__cell--${cell}` : "",
              winLine?.includes(i) ? "ttt__cell--win" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-testid={`ttt-cell-${i}`}
            disabled={!active || cell !== null || !game.you.your_turn || busy}
            aria-label={
              cell
                ? formatCopy(resolveCopy("games.ttt_cell_taken") ?? "Square {n}, {mark}", {
                    n: i + 1,
                    mark: cell.toUpperCase(),
                  })
                : formatCopy(resolveCopy("games.ttt_cell_empty") ?? "Square {n}, empty", { n: i + 1 })
            }
            onClick={() => act({ cell: i })}
          >
            {cell === "x" && <PixelCross size={24} />}
            {cell === "o" && <PixelHeart size={24} />}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// result views (terminal COMPLETED) — Ticker's verdict + the burst
// ---------------------------------------------------------------------------

function CompletedView({ game, myId, partnerName }: { game: GameWire; myId: string; partnerName: string }) {
  const result = game.result ?? {};
  let mood: MascotMood = "happy";
  let line = "";
  let celebrate = false;
  let detail: ReactNode = null;

  if (game.kind === "this_or_that") {
    const agreed = (result.agreed as number | undefined) ?? 0;
    const total = (result.total as number | undefined) ?? game.this_or_that?.total_rounds ?? 5;
    line = (result.score_line as string | undefined) ?? "";
    mood = agreed >= Math.ceil(total / 2) ? "cheer" : "wink";
    celebrate = agreed >= Math.ceil(total / 2);
    const differed = (result.differed as number[] | undefined) ?? [];
    const revealed = game.this_or_that?.revealed ?? [];
    if (differed.length > 0) {
      detail = (
        <div className="gamecard__differed">
          <p className="text-faint">{resolveCopy("games.differed_intro")}</p>
          <ul className="gamecard__differed-list">
            {differed.map((round) => {
              const r = revealed.find((x) => x.round === round);
              if (!r) return null;
              const mine = r.picks[myId];
              return (
                <li key={round}>
                  {mine === "a" ? r.prompt.option_a : r.prompt.option_b}
                  <span className="text-faint"> ({resolveCopy("games.you_marker")})</span>
                  {" · "}
                  {mine === "a" ? r.prompt.option_b : r.prompt.option_a}
                  <span className="text-faint"> ({resolveCopy("games.them_marker")})</span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    }
  } else if (game.kind === "two_truths") {
    const correct = Boolean(result.correct);
    const lieIndex = result.lie_index as number | undefined;
    const guessIndex = result.guess_index as number | undefined;
    line = (result.line as string | undefined) ?? "";
    // The card cheers for the viewer: the guesser wins by calling it, the
    // author wins when the lie survives.
    const viewerWon = game.you.is_creator ? !correct : correct;
    mood = viewerWon ? "cheer" : game.you.is_creator ? "error" : "wink";
    celebrate = viewerWon;
    const statements = game.two_truths?.statements ?? [];
    detail = (
      <ol className="tt__list tt__list--result">
        {statements.map((s, i) => (
          <li
            key={i}
            className={`tt__statement tt__statement--readonly ${i === lieIndex ? "tt__statement--lie" : ""}`}
          >
            {s}
            {i === lieIndex && (
              <em className="tt__marker type-pixel">
                <PixelCross size={9} /> {resolveCopy("games.tt_lie_marker")}
              </em>
            )}
            {i === guessIndex && (
              <em className="tt__marker tt__marker--guess type-pixel">
                {resolveCopy(game.you.is_creator ? "games.tt_guess_marker" : "games.tt_your_guess_marker")}
              </em>
            )}
          </li>
        ))}
      </ol>
    );
  } else {
    const winner = (result.winner as string | null | undefined) ?? null;
    const draw = Boolean(result.draw);
    if (draw) {
      line = resolveCopy("games.ttt_draw") ?? "";
      mood = "happy";
    } else if (winner === myId) {
      line = resolveCopy("games.ttt_win_you") ?? "";
      mood = "cheer";
      celebrate = true;
    } else {
      line = formatCopy(resolveCopy("games.ttt_win") ?? "{name} takes the board. GG.", { name: partnerName });
      mood = "heartbroken";
    }
    detail = <TicTacToeBoard game={game} myId={myId} partnerName={partnerName} busy={true} act={() => {}} />;
  }

  return (
    <div className="gamecard__result" data-testid="game-result">
      {celebrate && justHappened(game) && <ResultBurst />}
      <motion.span
        className="gamecard__result-mascot"
        variants={bounceIn}
        initial={justHappened(game) ? "initial" : false}
        animate="enter"
      >
        <Mascot mood={mood} size={42} />
      </motion.span>
      {line && <p className="gamecard__result-line type-editorial">{line}</p>}
      {detail}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <GameThreadCard/> — one game, in the conversation, hosted by Ticker
// ---------------------------------------------------------------------------

export interface GameThreadCardProps {
  game: GameWire;
  myId: string;
  partnerName: string;
  onUpdate: (g: GameWire) => void;
}

const CLOSED_LINES: Partial<Record<GameStatusWire, { key: string; mood: MascotMood }>> = {
  expired: { key: "games.invite_expired", mood: "sleepy" },
  abandoned: { key: "games.game_abandoned", mood: "idle" },
};

export function GameThreadCard({ game, myId, partnerName, onUpdate }: GameThreadCardProps) {
  const [busy, setBusy] = useState(false);
  const reportedRef = useRef(false);

  const respond = useCallback(
    async (action: "accept" | "decline") => {
      setBusy(true);
      await postRespond(game.game_id, action, onUpdate);
      setBusy(false);
    },
    [game.game_id, onUpdate],
  );

  const act = useCallback(
    (move: { pick?: "a" | "b"; guess_index?: number; cell?: number }) => {
      setBusy(true);
      void postAct(game.game_id, move, onUpdate).finally(() => setBusy(false));
    },
    [game.game_id, onUpdate],
  );

  const report = useCallback(async () => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    try {
      await api.post("/reports", { body: { category: "other", game_id: game.game_id } as never });
      toast(resolveCopy("safety.report_ack") ?? "Report received.", "success");
    } catch (err) {
      reportedRef.current = false;
      if (isApiError(err)) toast(err.message, "error");
    }
  }, [game.game_id]);

  const label = KIND_LABELS[game.kind];
  const isInvite = game.status === "invited";
  const closed = CLOSED_LINES[game.status];

  let body: ReactNode;
  if (isInvite) {
    body = game.you.is_creator ? (
      <div className="gamecard__invite">
        <Mascot mood="idle" size={34} />
        <p className="gamecard__line type-editorial">{resolveCopy("games.invite_sent")}</p>
      </div>
    ) : (
      <div className="gamecard__invite">
        <Mascot mood="happy" size={34} />
        <p className="gamecard__line type-editorial">
          {formatCopy(resolveCopy("games.invite_line") ?? "{name} wants to play {game}.", {
            name: partnerName,
            game: label,
          })}
        </p>
        <p className="text-faint gamecard__hint">{resolveCopy("games.respond_hint")}</p>
        <div className="gamecard__actions">
          <Button
            variant="ghost"
            size="sm"
            data-testid="game-decline"
            disabled={busy}
            onClick={() => void respond("decline")}
          >
            {resolveCopy("games.decline_label") ?? "Decline"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="game-accept"
            loading={busy}
            onClick={() => void respond("accept")}
          >
            {resolveCopy("games.accept_label") ?? "Accept"}
          </Button>
        </div>
      </div>
    );
  } else if (game.status === "active") {
    const boardProps: BoardProps = { game, myId, partnerName, busy, act };
    body =
      game.kind === "this_or_that" ? (
        <ThisOrThatBoard {...boardProps} />
      ) : game.kind === "two_truths" ? (
        <TwoTruthsBoard {...boardProps} />
      ) : (
        <TicTacToeBoard {...boardProps} />
      );
  } else if (game.status === "completed") {
    body = <CompletedView game={game} myId={myId} partnerName={partnerName} />;
  } else if (game.status === "declined") {
    body = (
      <div className="gamecard__closed">
        <Mascot mood="heartbroken" size={30} />
        <p className="text-dim">
          {resolveCopy(game.you.is_creator ? "games.declined_line" : "games.declined_by_you")}
        </p>
      </div>
    );
  } else if (closed) {
    body = (
      <div className="gamecard__closed">
        <Mascot mood={closed.mood} size={30} />
        <p className="text-dim">{resolveCopy(closed.key)}</p>
      </div>
    );
  }

  const showReport = game.status === "active" || game.status === "completed";

  return (
    <motion.div
      className={`gamecard gamecard--${game.status}`}
      data-testid={isInvite ? "game-invite" : "game-card"}
      data-kind={game.kind}
      data-status={game.status}
      initial={{ opacity: 0, y: 14, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={springs.bouncy}
    >
      <div className="gamecard__head">
        <span className="gamecard__head-icon" aria-hidden="true">
          <KindIcon kind={game.kind} size={16} />
        </span>
        <span className="gamecard__head-label type-pixel">{label}</span>
        {game.kind === "this_or_that" && game.status === "active" && game.this_or_that && (
          <span className="gamecard__head-sub type-pixel text-faint">
            {formatCopy(resolveCopy("games.round_counter") ?? "Round {n} of {total}", {
              n: Math.min(game.this_or_that.round + 1, game.this_or_that.total_rounds),
              total: game.this_or_that.total_rounds,
            })}
          </span>
        )}
        {(game.status === "completed" || game.status === "declined" || Boolean(closed)) && (
          <span className="gamecard__head-sub type-pixel text-faint">
            {resolveCopy(game.status === "completed" ? "games.result_kicker" : "games.closed_kicker")}
          </span>
        )}
      </div>
      {body}
      {showReport && (
        // Safety affordance — quiet, factual, never gated (MISSION §2).
        <button className="gamecard__report" data-testid="game-report" onClick={() => void report()}>
          {resolveCopy("games.report_game") ?? "Report this game"}
        </button>
      )}
    </motion.div>
  );
}
